/**
 * st-buy-total.js — ST 2026-09-24 (Wrike 4566366996)
 *
 * Total price above the main add-to-cart button: main variant x quantity plus every
 * checked extra x data-min. Same inputs and quantities as ProductForm.prepareFormData,
 * so the shown total matches what goes into the cart.
 */

if (!customElements.get("st-buy-total")) {
  customElements.define(
    "st-buy-total",
    class StBuyTotal extends HTMLElement {
      connectedCallback() {
        this.formId = this.getAttribute("form");
        this.form = document.getElementById(this.formId);
        this.update = this.update.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onChange = this.onChange.bind(this);

        const target = this.form || document;
        target.addEventListener("bundle:change", this.update);
        target.addEventListener("st-services:change", this.update);
        target.addEventListener("variant:change", this.onVariantChange);
        document.addEventListener("change", this.onChange);

        // Variant changes re-render the ProductBundle / MembershipOffer markup (new inputs).
        this.swapObserver = new MutationObserver(this.update);
        document
          .querySelectorAll(`[id^="ProductBundle-"], [id^="MembershipOffer-"]`)
          .forEach((el) => {
            this.swapObserver.observe(el, { childList: true, subtree: true });
          });

        this.update();

        // Wrike 4585754742 round 4: refit the pills of the compact mobile box when its width changes.
        this.fitPills = this.fitPills.bind(this);
        this.boxWidth = 0;
        this.resizeObserver = new ResizeObserver((entries) => {
          const width = Math.round(entries[0].contentRect.width);
          if (width === this.boxWidth) return;
          this.boxWidth = width;
          this.fitPills();
        });
        this.resizeObserver.observe(this);
        document.fonts?.ready.then(this.fitPills);
      }

      disconnectedCallback() {
        const target = this.form || document;
        target.removeEventListener("bundle:change", this.update);
        target.removeEventListener("st-services:change", this.update);
        target.removeEventListener("variant:change", this.onVariantChange);
        document.removeEventListener("change", this.onChange);
        if (this.swapObserver) this.swapObserver.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      get checkedExtras() {
        return Array.from(
          document.querySelectorAll(
            `[form="${this.formId}"] input[name="bundles"]:checked`,
          ),
        );
      }

      // Wrike 4586442589: price of the PDP service checkbox (snippets/st-pdp-services) per device, 0 when none applies.
      get servicePrice() {
        const services = document.querySelector(
          `st-pdp-services[form="${this.formId}"]`,
        );
        const service = services?.selection
          ? services.selection(this.getAttribute("data-variant-id"))
          : null;
        return service ? service.price : 0;
      }

      get quantity() {
        const input = document.querySelector(
          `input[name="quantity"][form="${this.formId}"]`,
        );
        return Math.max(parseInt(input?.value, 10) || 1, 1);
      }

      onVariantChange(event) {
        const variant = event.detail?.variant;
        if (!variant) return;
        // Round 7 (Wrike 4543052992): Refurbished = no monthly rate, same title rule as the Liquid side.
        // Wrike 4577510446 (Round 11, Ali): reference price only from data-ref-prices
        // ("<variant id>:<cents>:<deal|neu>,…", snippets/st-ref-price.liquid); Neu reference = no "*".
        const refurbished = /refurbished/i.test(variant.title || "");
        const entry = (this.getAttribute("data-ref-prices") || "")
          .split(",")
          .find((item) => item.split(":")[0] === String(variant.id));
        const [, cents, kind] = entry ? entry.split(":") : [];
        const reference = parseInt(cents, 10) || 0;
        this.toggleAttribute("data-refurbished", refurbished);
        this.toggleAttribute("data-neu-reference", kind === "neu");
        this.setAttribute("data-price", variant.price);
        this.setAttribute("data-variant-id", variant.id);
        this.setAttribute(
          "data-compare-at",
          reference > variant.price ? reference : 0,
        );
        this.update();
      }

      onChange(event) {
        const input = event.target;
        if (
          input.name === "quantity" &&
          input.getAttribute("form") === this.formId
        )
          this.update();
      }

      getTotals() {
        const quantity = this.quantity;
        const basePrice = parseInt(this.getAttribute("data-price"), 10) || 0;
        const baseCompareAt =
          parseInt(this.getAttribute("data-compare-at"), 10) || 0;
        const extras = this.checkedExtras;
        // Wrike 4586442589: service × quantity is in the total and in the strike-through sum (so the saving stays the
        // same); it never counts as an extra and not toward the monthly rate.
        const service = this.servicePrice * quantity;

        // Wrike 4566366996 round 12 (Ali 03.10., variant B2): accessory page (data-bundle-self), the checkout's
        // anchor rule in assets/theme.js (window.stBundleTotals): the most expensive prerequisite piece stays full,
        // every other bundle piece pays the bundle price, also the 2nd+ piece of the page product.
        if (this.hasAttribute("data-bundle-self") && window.stBundleTotals) {
          const totals = window.stBundleTotals(
            {
              price: basePrice,
              compareAt: baseCompareAt,
              quantity,
              bundle: window.stBundleSelfPrice(
                this,
                this.getAttribute("data-variant-id"),
              ),
              bundleMax:
                parseInt(this.getAttribute("data-bundle-max"), 10) || 0,
            },
            extras,
          );
          return {
            ...totals,
            price: totals.price + service,
            compareAt: totals.compareAt + service,
            service,
            count: extras.length,
          };
        }

        let price = basePrice * quantity;
        let compareAt = Math.max(baseCompareAt, basePrice) * quantity;

        // Wrike 4566366996: bundle-priced accessories (data-bundle-max = max_teile) count at most that many
        // pieces at the bundle price; pieces above pay the single price (data-single) and save only the UVP part.
        let bundleLeft = null;
        extras.forEach((input) => {
          const itemQuantity =
            parseInt(input.getAttribute("data-min"), 10) || 1;
          const itemPrice = parseInt(input.getAttribute("data-price"), 10) || 0;
          const itemCompareAt =
            parseInt(input.getAttribute("data-compare-at"), 10) || 0;
          const bundleMax =
            parseInt(input.getAttribute("data-bundle-max"), 10) || 0;
          let discounted = itemQuantity;
          if (bundleMax > 0) {
            if (bundleLeft === null) bundleLeft = bundleMax;
            discounted = Math.min(itemQuantity, bundleLeft);
            bundleLeft -= discounted;
          }
          const itemFull = Math.max(itemCompareAt, itemPrice);
          // Round 20 (Ali 10.10.): data-compare-at = UVP; pieces above max_teile pay data-single.
          const itemSingle =
            parseInt(input.getAttribute("data-single"), 10) || itemFull;
          price +=
            itemPrice * discounted + itemSingle * (itemQuantity - discounted);
          compareAt += itemFull * itemQuantity;
        });

        return {
          price: price + service,
          compareAt: compareAt + service,
          service,
          count: extras.length,
        };
      }

      update() {
        const { price, compareAt, count, service } = this.getTotals();
        const format = theme.settings.currencyCodeEnabled
          ? theme.settings.moneyWithCurrencyFormat
          : theme.settings.moneyFormat;
        const money = (amount) => theme.Currency.formatMoney(amount, format);
        // Wrike 4585754610 (Ali 17.10.): "Du sparst" = strike-through sum - shown total. A coupon code applied only at
        // checkout does not count (reverts the coupon share of Wrike 4566366996 round 23).
        const reduced = compareAt > price;
        const saving = compareAt - price;

        const priceEl = this.querySelector("[data-buy-total-price]");
        const compareEl = this.querySelector("[data-buy-total-compare]");
        const savingEl = this.querySelector("[data-buy-total-saving]");
        const countEl = this.querySelector("[data-buy-total-count]");
        const countText = this.querySelector("[data-buy-total-count-text]");

        const priceText = money(price);
        if (priceEl.textContent !== priceText) priceEl.textContent = priceText;
        // Wrike 4585754742 round 2: the mobile box sizes the total by its length (assets/st-buy-total.css)
        priceEl.parentElement.style.setProperty(
          "--st-buy-total-chars",
          priceText.length,
        );

        // "*" to the 30-day best-price note: only while the main variant itself is reduced.
        const starEl = this.querySelector("[data-buy-total-star]");
        if (starEl) {
          starEl.hidden =
            this.hasAttribute("data-neu-reference") ||
            !(
              parseInt(this.getAttribute("data-compare-at"), 10) >
              parseInt(this.getAttribute("data-price"), 10)
            );
        }

        compareEl.hidden = !reduced;
        savingEl.hidden = !reduced;
        if (reduced) {
          compareEl.textContent = money(compareAt);
          this.savingTexts = [this.dataset.saving, this.dataset.savingShort]
            .filter(Boolean)
            .map((text) => text.replace("[amount]", money(saving)));
          savingEl.textContent = this.savingTexts[0] || "";
        }

        this.updateFinancing(price - service, money);
        this.updateKlarna(money);
        this.updateAddons();

        // Wrike 4566366996 (Ali 02.10.): deal PDPs show the saving only with extras (assets/st-weekly-deal.css).
        this.toggleAttribute("data-extras", count > 0);
        countEl.hidden = count === 0;
        if (count > 0) {
          const template =
            count === 1 ? this.dataset.countOne : this.dataset.countOther;
          countText.textContent = (template || "").replace("[count]", count);
          // Wrike 4583777038: compact mobile box says "N Extras enthalten".
          const included = this.querySelector(
            "[data-buy-total-count-included]",
          );
          if (included) {
            const label =
              count === 1
                ? this.dataset.includedOne
                : this.dataset.includedOther;
            const short =
              count === 1
                ? this.dataset.includedShortOne
                : this.dataset.includedShortOther;
            this.includedTexts = [label, short]
              .filter(Boolean)
              .map((text) => text.replace("[count]", count));
            included.textContent = this.includedTexts[0] || "";
          }
        }

        if (this.fitPills) this.fitPills();
      }

      // Wrike 4585754742 round 4 (Ali/Argus 17.10.): in the compact mobile box both pills stay on one line at the
      // live size. Only a pill wider than its column (320 px, long CH/FR amounts) gets smaller, down to 10 px, and
      // after that the short text ("−296,00 €", "+1 Extra") instead of wrapping or crossing the line.
      fitPills() {
        const column = this.querySelector(".st-buy-total__prices");
        const label = this.querySelector(".st-buy-total__label");
        if (!column || !label) return;
        const compact = getComputedStyle(label).display !== "none";
        const fit = (pill, textEl, texts) => {
          if (!pill || !textEl || !texts?.length) return;
          pill.style.removeProperty("font-size");
          if (textEl.textContent !== texts[0]) textEl.textContent = texts[0];
          if (!compact || pill.hidden) return;
          const max = column.getBoundingClientRect().width;
          // fractional widths: offsetWidth rounds and lets a pill end up to 0.5 px past the column
          const fits = () =>
            pill.getBoundingClientRect().width <= max &&
            pill.scrollWidth <= pill.clientWidth;
          const base = parseFloat(getComputedStyle(pill).fontSize);
          for (const text of texts) {
            if (textEl.textContent !== text) textEl.textContent = text;
            pill.style.removeProperty("font-size");
            if (fits()) return;
            for (let size = Math.floor(base * 2) / 2; size >= 10; size -= 0.5) {
              pill.style.fontSize = `${size}px`;
              if (fits()) return;
            }
          }
        };
        const savingEl = this.querySelector("[data-buy-total-saving]");
        fit(savingEl, savingEl, this.savingTexts);
        fit(
          this.querySelector("[data-buy-total-count]"),
          this.querySelector("[data-buy-total-count-included]"),
          this.includedTexts,
        );
      }

      // Wrike 4585760065: names of the checked extras that carry data-st-name (compact "Beliebtes Zubehör" row with
      // the Live card), e.g. "inkl. Live (2 Jahre), Bodenschutzmatte". Other PDPs have no names: the line stays hidden.
      updateAddons() {
        const line = this.querySelector("[data-buy-total-addons]");
        if (!line) return;
        const names = this.checkedExtras
          .map((input) => input.getAttribute("data-st-name"))
          .filter(Boolean);
        line.hidden = names.length === 0;
        line.textContent = names.length
          ? (line.dataset.template || "").replace("[items]", names.join(", "))
          : "";
      }

      // Shop currency -> active currency factor for the financing limits (1 if unknown or same currency).
      limitRate(row) {
        const currency = window.Shopify?.currency;
        if (!currency || currency.active === row.dataset.limitCurrency)
          return 1;
        const rate = parseFloat(currency.rate);
        return rate > 0 ? rate : 1;
      }

      // Wrike 4585849025 (Ali/Iris 18.10., FR/ES): Klarna box "3 × amount" (a) below the Klarna limit, neutral
      // "Paiement sécurisé" hint (u) from the limit on, nothing for Refurbished (off). Price = the variant price
      // (data-price, without the code price), amount = price / 3 rounded to the cent like Liquid's `round`.
      updateKlarna(money) {
        const box = this.querySelector("[data-buy-total-klarna]");
        if (!box) return;
        const price = parseInt(this.getAttribute("data-price"), 10) || 0;
        const limit = Math.round(
          (parseInt(box.dataset.limit, 10) || 0) * this.limitRate(box),
        );
        const refurbished = this.hasAttribute("data-refurbished");
        let mode = "a";
        if (refurbished) mode = "off";
        else if (limit > 0 && price >= limit) mode = "u";
        box.hidden = refurbished;
        this.setAttribute("data-klarna-mode", mode);
        if (mode !== "a") return;

        const amount = money(Math.round(price / 3));
        if (box.dataset.amount === amount) return;
        box.dataset.amount = amount;
        box.querySelectorAll("[data-klarna-amount]").forEach((el) => {
          el.textContent = amount;
        });
        const text = box.querySelector("[data-klarna-text]");
        // The template is a translated locale string (trusted); the amount comes from formatMoney.
        if (text)
          text.innerHTML = (box.dataset.template || "").replace(
            "[amount]",
            amount,
          );
      }

      // Monthly rate of the total, floored like Liquid's `divided_by` on the variant price.
      updateFinancing(price, money) {
        // Wrike 4578271229 round 2: Klarna "in 3 Raten" row (no rate), hidden like the rate for Refurbished.
        const pay3 = this.querySelector("[data-buy-total-pay3]");
        if (pay3) pay3.hidden = this.hasAttribute("data-refurbished");
        const row = this.querySelector("[data-buy-total-financing]");
        if (!row) return;
        const months = parseInt(row.dataset.months, 10) || 0;
        // Wrike 4578271229 round 3: the limits are shop-currency (EUR) amounts. In another
        // currency (CH) they are converted with the shop's exchange rate before comparing.
        const limitRate = this.limitRate(row);
        const threshold = Math.round(
          (parseInt(row.dataset.threshold, 10) || 0) * limitRate,
        );
        // Wrike 4578271229: no rate above the financing maximum (0 = no maximum).
        const max = Math.round(
          (parseInt(row.dataset.max, 10) || 0) * limitRate,
        );
        row.hidden =
          months <= 0 ||
          price < threshold ||
          (max > 0 && price > max) ||
          this.hasAttribute("data-refurbished");
        if (row.hidden) return;

        const rateEl = row.querySelector("[data-buy-total-rate]");
        const rate = money(Math.floor(price / months));
        if (rateEl.dataset.rate === rate) return;
        rateEl.dataset.rate = rate;
        // Wrike 4583777038: rate amount of the compact mobile box.
        const amountEl = row.querySelector("[data-buy-total-rate-amount]");
        if (amountEl) amountEl.textContent = rate;
        // The template is a translated locale string (trusted); the amount comes from formatMoney.
        rateEl.innerHTML = (row.dataset.template || "").replace(
          "[price]",
          rate,
        );
      }
    },
  );
}
